import React, { memo } from 'react';
import { FaUserNurse, FaClock, FaCalendarAlt, FaWhatsapp, FaPhone } from 'react-icons/fa';
import { HIRING_OPTIONS, STRUCTURED_DATA } from '../constants';
import { MAIN_SERVICES } from '../data';
import { useWhatsApp, usePhone } from '../hooks';
import { ServiceCard } from './common';

// Mapeo de iconos para los servicios
const iconMap = {
  FaUserNurse,
  FaClock,
  FaCalendarAlt
};

const HiringOptionButton = memo(({ option, onClick }) => (
  <button
    onClick={onClick}
    className={`bg-gradient-to-r ${option.color} text-white rounded-xl py-4 px-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex flex-col items-center justify-center text-center focus:outline-none focus:ring-4 focus:ring-blue-300 w-full`}
    aria-label={`Solicitar plan de atención de ${option.hours} por WhatsApp`}
  >
    <FaWhatsapp className="text-white text-2xl mb-2" aria-hidden="true" />
    <span className="font-bold text-lg">{option.hours}</span>
    <span className="text-xs opacity-80 mt-1">Solicitar por WhatsApp</span>
  </button>
));

HiringOptionButton.displayName = 'HiringOptionButton';

function Services() {
  const { openHiringInquiry } = useWhatsApp();
  const { callCompany } = usePhone();

  // Servicios con iconos mapeados
  const services = MAIN_SERVICES.map(service => ({
    ...service,
    icon: iconMap[service.icon] || FaUserNurse
  }));

  return (
    <>
      {/* Datos estructurados para SEO */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA) }}
      />
      
      <section 
        aria-labelledby="services-heading" 
        className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50"
        itemScope
        itemType="https://schema.org/MedicalBusiness"
      >
        <div className="max-w-7xl mx-auto">
          {/* Header mejorado para SEO */}
          <header className="text-center mb-12">
            <h1
              id="services-heading"
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              itemProp="name"
            >
              Enfermeras y Cuidadoras Certificadas para tu Familiar en Guadalajara
            </h1>
            <p 
              className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed"
              itemProp="description"
            >
              Atención médica especializada con enfermeras tituladas y certificadas, 
              planes de cuidado VIP personalizados y supervisión clínica continua en la comodidad de su hogar
            </p>
          </header>

          {/* Grid de servicios con mejor accesibilidad */}
          <div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 place-items-center mb-16"
            role="list"
            aria-label="Lista de servicios de enfermería"
          >
            {services.map((service, index) => (
              <div key={index} role="listitem">
                <ServiceCard
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  index={index}
                />
              </div>
            ))}
          </div>
          
          {/* Banner de contratación mejorado */}
          <div className="bg-gradient-to-r from-blue-50 via-blue-100 to-gray-50 rounded-3xl p-8 md:p-12 shadow-2xl max-w-6xl mx-auto border border-blue-100">
            <div className="flex flex-col items-center w-full">
              <div className="text-center w-full">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Planes de Atención a la Medida
                </h2>
                <p className="text-lg md:text-xl text-gray-700 mb-8 mx-auto max-w-3xl">
                  Seleccione la modalidad de atención que mejor se adapte a las necesidades de su familiar. 
                  Nuestro equipo de enfermeras certificadas, con riguroso control de confianza, garantiza 
                  estándares hospitalarios premium en la privacidad de su hogar.
                </p>
                
                {/* Grid de opciones de contratación */}
                <div 
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
                  role="list"
                  aria-label="Opciones de horarios disponibles"
                >
                  {HIRING_OPTIONS.map((option, index) => (
                    <div key={index} role="listitem">
                      <HiringOptionButton
                        option={option}
                        onClick={() => openHiringInquiry(option.hours)}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Información adicional de contacto */}
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 mb-6">
                   <p className="text-blue-800 font-semibold text-lg mb-4">
                    Diseñamos un plan de cuidado exclusivo para cada paciente.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                      onClick={callCompany}
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors focus:outline-none focus:ring-4 focus:ring-blue-300"
                      aria-label="Llamar para consulta personalizada"
                    >
                      <FaPhone className="text-sm" />
                      Consulta Personalizada
                    </button>
                    <span className="text-gray-600">
                      o contáctenos por WhatsApp para coordinar su plan de atención
                    </span>
                  </div>
                </div>

                {/* Sellos de confianza para SEO */}
                <div className="text-sm text-gray-600 space-x-2 flex flex-wrap justify-center">
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Enfermería certificada</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Estándares hospitalarios</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Atención VIP 24/7</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Control de confianza riguroso</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Plan de cuidado personalizado</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;
